// client/src/pages/admin/AdminHeroSlider.jsx

import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import Swal from 'sweetalert2';
import { buildAssetUrl } from '../../utils/url';

const AdminHeroSlider = () => {
  // ─── State ──────────────────────────────────────────────────────────────────
  const [heroSliders, setHeroSliders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal controls:
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentHeroSlider, setCurrentHeroSlider] = useState(null);

  // Form data:
  const [formData, setFormData] = useState({
    title: '',
    image: null,
    imagePreview: '',
    button_text: 'KNOW MORE',
    button_link: '/categories',
    order: 0,
    isActive: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── On Mount: fetch hero sliders ──────────────────────────────────────────────
  useEffect(() => {
    fetchHeroSliders();
  }, []);

  // ─── Fetch All Hero Sliders ───────────────────────────────────────────────────
  const fetchHeroSliders = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/hero-sliders');
      setHeroSliders(data);
    } catch (err) {
      console.error('Error loading hero sliders:', err);
      Swal.fire('Error', 'Failed to load hero sliders', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Open "Add New Hero Slider" Modal ───────────────────────────────────────────
  const openAddModal = () => {
    setEditMode(false);
    setCurrentHeroSlider(null);
    setFormData({
      title: '',
      image: null,
      imagePreview: '',
      button_text: 'KNOW MORE',
      button_link: '/categories',
      order: 0,
      isActive: true
    });
    setIsModalOpen(true);
  };

  // ─── Open "Edit Hero Slider" Modal ──────────────────────────────────────────────
  const openEditModal = (heroSlider) => {
    setEditMode(true);
    setCurrentHeroSlider(heroSlider);

    // Pre‐fill the form with existing data
    const imagePreviewUrl = buildAssetUrl(heroSlider.image_path || '');

    setFormData({
      title: heroSlider.title || '',
      image: null,
      imagePreview: imagePreviewUrl,
      button_text: heroSlider.button_text || 'KNOW MORE',
      button_link: heroSlider.button_link || '/categories',
      order: heroSlider.order || 0,
      isActive: heroSlider.isActive !== false
    });
    setIsModalOpen(true);
  };

  // ─── Close Modal & Reset ─────────────────────────────────────────────────────
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentHeroSlider(null);
    setFormData({
      title: '',
      image: null,
      imagePreview: '',
      button_text: 'KNOW MORE',
      button_link: '/categories',
      order: 0,
      isActive: true
    });
  };

  // ─── Handle Form Changes ─────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(fd => ({
      ...fd,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // ─── Handle Image Selection ───────────────────────────────────────────────────
  const handleImageChange = (e) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      Swal.fire('Error', 'Please select an image file', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire('Error', 'Image size should be less than 5MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(fd => ({
        ...fd,
        image: file,
        imagePreview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  // ─── Upload Single File ─────────────────────────────────────────────────────
  const uploadSingleFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post('/upload/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data.path;
  };

  // ─── Handle Form Submit ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      Swal.fire('Error', 'Title is required', 'error');
      return;
    }

    if (!editMode && !formData.image) {
      Swal.fire('Error', 'Image is required for new hero sliders', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      let imagePath = '';

      // Upload image if selected
      if (formData.image) {
        imagePath = await uploadSingleFile(formData.image);
      }

      const heroSliderData = {
        title: formData.title.trim(),
        button_text: formData.button_text.trim(),
        button_link: formData.button_link.trim(),
        order: parseInt(formData.order) || 0,
        isActive: formData.isActive
      };

      if (imagePath) heroSliderData.image_path = imagePath;

      if (editMode) {
        // Update existing hero slider
        await api.put(`/hero-sliders/${currentHeroSlider.id}`, heroSliderData);
        Swal.fire('Success', 'Hero slider updated successfully', 'success');
      } else {
        // Create new hero slider
        await api.post('/hero-sliders', heroSliderData);
        Swal.fire('Success', 'Hero slider created successfully', 'success');
      }

      closeModal();
      fetchHeroSliders();
    } catch (err) {
      console.error('Error saving hero slider:', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to save hero slider', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Handle Delete ───────────────────────────────────────────────────────────
  const handleDelete = (heroSlider) => {
    Swal.fire({
      title: 'Delete Hero Slider',
      text: `Are you sure you want to delete "${heroSlider.title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/hero-sliders/${heroSlider.id}`);
          Swal.fire('Deleted!', 'Hero slider has been deleted.', 'success');
          fetchHeroSliders();
        } catch (err) {
          console.error('Error deleting hero slider:', err);
          Swal.fire('Error', 'Failed to delete hero slider', 'error');
        }
      }
    });
  };

  // ─── Toggle Active Status ───────────────────────────────────────────────────
  const toggleActive = async (heroSlider) => {
    try {
      await api.patch(`/hero-sliders/${heroSlider.id}`, {
        isActive: !heroSlider.isActive
      });
      fetchHeroSliders();
    } catch (err) {
      console.error('Error toggling status:', err);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Hero Slider Management</h1>
        <button
          onClick={openAddModal}
          className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          Add New Hero Slider
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hero Slider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {heroSliders.length > 0 ? (
                  heroSliders.map((heroSlider) => (
                    <tr key={heroSlider.id} className="hover:bg-gray-50">
                      {/* Hero Slider Info */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-16 w-24">
                            {heroSlider.image_path ? (
                              <img
                                className="h-16 w-24 rounded-lg object-cover"
                                src={buildAssetUrl(heroSlider.image_path)}
                                alt={heroSlider.title}
                              />
                            ) : (
                              <div className="h-16 w-24 rounded-lg bg-gray-200 flex items-center justify-center">
                                <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {heroSlider.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              {heroSlider.button_text} → {heroSlider.button_link}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Title */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {heroSlider.title}
                        </div>
                      </td>

                      {/* Order */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {heroSlider.order}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleActive(heroSlider)}
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            heroSlider.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {heroSlider.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => openEditModal(heroSlider)}
                          className="text-black hover:text-gray-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(heroSlider)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      No hero sliders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">
              {editMode ? 'Edit Hero Slider' : 'Add New Hero Slider'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hero Slider Image {!editMode && '*'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required={!editMode}
                />
                {formData.imagePreview && (
                  <img
                    src={formData.imagePreview}
                    alt="Preview"
                    className="mt-2 h-20 w-32 object-cover rounded-lg"
                  />
                )}
              </div>

              {/* Button Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Text
                </label>
                <input
                  type="text"
                  name="button_text"
                  value={formData.button_text}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>

              {/* Button Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Link
                </label>
                <input
                  type="text"
                  name="button_link"
                  value={formData.button_link}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHeroSlider;
